import { Volume2, VolumeX } from '@music-vine/cadence/icons';
import { useCallback, useEffect, useReducer, useState } from 'react';
import { Button } from '@music-vine/cadence/ui';
import { Popover, PopoverContent, PopoverTrigger } from '@music-vine/cadence/ui';
import { Slider } from '@music-vine/cadence/ui';
import { cn } from '@/lib/utils';
interface VolumeSliderProps {
  disabled?: boolean;
  muted?: boolean;
  onVolumeChange: (volume: number[]) => void;
  orientation?: 'horizontal' | 'vertical';
  volume: number;
}
interface VolumeState {
  isMuted: boolean;
  volume: number;
}
type VolumeAction = {
  type: 'SYNC_STATE';
  volume: number;
  muted: boolean;
} | {
  type: 'SET_VOLUME_AND_MUTE';
  volume: number;
} | {
  type: 'TOGGLE_MUTE';
  prevVolume: number;
};
const volumeReducer = (state: VolumeState, action: VolumeAction): VolumeState => {
  switch (action.type) {
    case 'SYNC_STATE':
      return {
        volume: action.volume,
        isMuted: action.muted
      };
    case 'SET_VOLUME_AND_MUTE':
      return {
        volume: action.volume,
        isMuted: action.volume === 0
      };
    case 'TOGGLE_MUTE':
      return {
        volume: state.isMuted ? state.volume === 0 ? 0.5 : state.volume : state.volume,
        isMuted: !state.isMuted
      };
    default:
      return state;
  }
};
const VolumeSlider = ({
  muted = true,
  orientation,
  onVolumeChange,
  disabled = false,
  volume: externalVolume
}: VolumeSliderProps) => {
  const [{
    volume,
    isMuted
  }, dispatch] = useReducer(volumeReducer, {
    volume: externalVolume,
    isMuted: muted
  });
  const [showSlider, setShowSlider] = useState(false);
  useEffect(() => {
    dispatch({
      type: 'SYNC_STATE',
      volume: externalVolume,
      muted
    });
  }, [externalVolume, muted]);
  const handleVolumeChange = (newVolume: number[]) => {
    const volumeValue = newVolume[0];
    dispatch({
      type: 'SET_VOLUME_AND_MUTE',
      volume: volumeValue
    });
    onVolumeChange([volumeValue]);
  };
  const handleDocumentWheel = useCallback((event: WheelEvent) => {
    if (showSlider) {
      event.preventDefault();
      const delta = event.deltaY > 0 ? -0.01 : 0.01;
      const newVolume = Math.max(0, Math.min(1, volume + delta));
      dispatch({
        type: 'SET_VOLUME_AND_MUTE',
        volume: newVolume
      });
      onVolumeChange([newVolume]);
    }
  }, [showSlider, volume, onVolumeChange]);
  useEffect(() => {
    document.addEventListener('wheel', handleDocumentWheel, {
      passive: false
    });
    return () => {
      document.removeEventListener('wheel', handleDocumentWheel);
    };
  }, [handleDocumentWheel]);
  const onToggleMute = () => {
    dispatch({
      type: 'TOGGLE_MUTE',
      prevVolume: volume
    });
    onVolumeChange([isMuted ? volume === 0 ? 0.5 : volume : 0]);
  };
  if (disabled) {
    return <Popover>
        <PopoverTrigger asChild>
          <Button className="text-white enabled:hover:bg-transparent" data-testid="volume-no-sound-button" size="icon" variant="transparent">
            <VolumeX className="h-4 w-4" />
            <span className="sr-only">This video has no audio</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" alignOffset={5} className="w-fit px-2 py-1" data-testid="volume-no-sound-popover" side="top">
          This video has no audio
        </PopoverContent>
      </Popover>;
  }
  return <div className={cn('flex flex-col-reverse items-center justify-center gap-3')} onPointerLeave={() => setShowSlider(false)} data-sentry-component="VolumeSlider" data-sentry-source-file="VolumeSlider.tsx">
      <Button className="text-white enabled:hover:bg-transparent" onClick={onToggleMute} onPointerEnter={() => setShowSlider(true)} size="icon" variant="transparent" data-sentry-element="Button" data-sentry-source-file="VolumeSlider.tsx">
        {isMuted || volume === 0 ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        <span className="sr-only">{isMuted ? 'Unmute' : 'Mute'}</span>
      </Button>

      <div className={cn('h-full w-full rounded-lg px-1 py-3 transition-opacity', showSlider ? 'bg-gray-50 opacity-100' : 'pointer-events-none opacity-0')}>
        <div className={cn('flex flex-row items-center justify-center', orientation === 'horizontal' ? 'h-full w-24' : 'h-24 w-full')}>
          <Slider aria-label="Volume slider" defaultValue={[1]} max={1} min={0} onBlur={() => setShowSlider(false)} onFocus={() => setShowSlider(true)} onValueChange={handleVolumeChange} orientation={orientation ?? 'vertical'} step={0.05} value={[isMuted ? 0 : volume]} data-sentry-element="Slider" data-sentry-source-file="VolumeSlider.tsx" />
        </div>
      </div>
    </div>;
};
export default VolumeSlider;